import { z } from "zod";

// Source of truth for the worker pipeline state machine. The api package binds
// these literals into a Postgres `job_status` pgEnum (see ADR-0018 amended ⁴);
// the worker and frontend import the union to stay in lockstep without pulling
// drizzle into either. `cancelled` is the re-upload backstop (ADR-0018 fn ⁸).
export const JOB_STATUSES = ["queued", "running", "succeeded", "failed", "cancelled"] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];
export const JobStatusSchema = z.enum(JOB_STATUSES);

// Pipeline stages the worker emits via `stage_started` events (ADR-0002 amended,
// ADR-0018 fn ⁹). Bound into the Postgres `pipeline_stage` pgEnum on the api side
// and projected onto `jobs.current_stage` so the manifest read is one row.
// NOTE: this order is the frozen wire/pgEnum order (Postgres can't reorder an
// enum), not the execution order — since ADR-0047 the worker actually runs
// upscale after segmentation + validation (see worker runner._run_pipeline).
export const PIPELINE_STAGES = [
  "nsfw",
  "detection",
  "upscale",
  "segmentation",
  "validation",
  "mesh",
  "back_generation",
] as const;
export type PipelineStage = (typeof PIPELINE_STAGES)[number];
export const PipelineStageSchema = z.enum(PIPELINE_STAGES);

// Catalogue enums for `product_variants` (see ADR-0016 + ADR-0018 amended ⁴).
// The api package binds these into the `pillow_shape` and `fabric_type`
// pgEnums; worker / frontend import the unions for editor + producer routing.
export const PILLOW_SHAPES = ["silhouette", "rectangle"] as const;
export type PillowShape = (typeof PILLOW_SHAPES)[number];
export const PillowShapeSchema = z.enum(PILLOW_SHAPES);

// MVP is a single material (#245): the catalogue collapses to one `default`
// fabric and the product page offers no material choice. Kept as an enum (not
// dropped) so re-introducing named fabrics later is an additive change + a
// migration, not a schema reshape.
export const FABRIC_TYPES = ["default"] as const;
export type FabricType = (typeof FABRIC_TYPES)[number];
export const FabricTypeSchema = z.enum(FABRIC_TYPES);

// Canonical inventory of artifacts the worker writes per project (ADR-0018
// amended ⁴, bound into the `asset_type` pgEnum on the api side).
//
// The trailing three are the producer-packet masters (#280, ADR-0021): the
// factory-facing artifacts the order packet is assembled from, NOT editor-manifest
// assets (so they are absent from ASSET_TYPE_TO_MANIFEST_KEY, like `og_image`).
// `cut_contour` is the Normalized-outline cut line (JSON, registered against the
// print crop); `print_front`/`print_back` are lossless PNG print masters derived
// from the decontaminated normalized master cutout. Append-only — the pgEnum order
// is frozen (Postgres can't reorder an enum), each addition is an ALTER TYPE.
export const ASSET_TYPES = [
  "mask",
  "thumbnail",
  "mesh_silhouette",
  "texture_front",
  "texture_back",
  "og_image",
  "cut_contour",
  "print_front",
  "print_back",
] as const;
export type AssetType = (typeof ASSET_TYPES)[number];
export const AssetTypeSchema = z.enum(ASSET_TYPES);

// Producer-packet file kinds tracked in `production_files` (#281, ADR-0021).
// The api assembles these per PAID order from the worker's `cut_contour` +
// `print_front`/`print_back` masters: the two pass-through print PNGs, the
// vector cut line as SVG (human-review underlay) + DXF (R12, mm — the sew line),
// and the PL spec sheet. Bound into the `production_file_kind` pgEnum on the api
// side; shared so the admin console can label/list the packet. Append-only (the
// pgEnum order is frozen — each addition is an ALTER TYPE).
export const PRODUCTION_FILE_KINDS = [
  "print_front",
  "print_back",
  "cut_svg",
  "cut_dxf",
  "spec_pdf",
] as const;
export type ProductionFileKind = (typeof PRODUCTION_FILE_KINDS)[number];
export const ProductionFileKindSchema = z.enum(PRODUCTION_FILE_KINDS);

// Who authored a `production_notes` row (#281): the `operator` (a note attached
// in the admin console) or the `factory` (a producer reply logged against the
// order). Bound into the `production_note_source` pgEnum on the api side.
export const PRODUCTION_NOTE_SOURCES = ["operator", "factory"] as const;
export type ProductionNoteSource = (typeof PRODUCTION_NOTE_SOURCES)[number];
export const ProductionNoteSourceSchema = z.enum(PRODUCTION_NOTE_SOURCES);

// Order lifecycle (ADR-0018 amended ⁴, bound into the `order_status` pgEnum).
// pending → paid → in_production → shipped → delivered, with refunded /
// cancelled as terminal sidetracks.
export const ORDER_STATUSES = [
  "pending",
  "paid",
  "in_production",
  "shipped",
  "delivered",
  "refunded",
  "cancelled",
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];
export const OrderStatusSchema = z.enum(ORDER_STATUSES);

// Order audit-trail event types (ADR-0018 revision 2026-07-06, logged to
// `order_events`). `event_type` is free `text` — not a pgEnum — so adding one
// needs no `ALTER TYPE`; the literal set is owned here and shared api-wide.
// `confirmation_email_sent` lands with the email slice (#227): recorded once
// the paid order's buyer confirmation + owner-notify have been dispatched.
// `shipped_email_sent` / `delivered_email_sent` are the same idempotency guard
// for the two lifecycle emails (#315): recorded once the buyer shipped/delivered
// mail has been dispatched (or skipped for a suppressed buyer), so a re-ticked
// fulfillment step can't double-send.
//
// `internal_note` (#244) is the operator's append-only notes thread — an event
// of this type carries a `{ text }` payload + the acting admin, surfaced as a
// dedicated thread on the order detail (its literal is owned by ./notes).
//
// The trailing block are the fulfillment-checklist steps (#243): the operator
// manually ticks physical-production steps on the admin order-detail screen and
// each is appended here with an actor. Adding/branching a step is a registry
// entry in ./fulfillment plus a literal here — no `ALTER TYPE`, no migration.
export const ORDER_EVENT_TYPES = [
  "order_created",
  "checkout_session_created",
  "payment_succeeded",
  "order_paid",
  "confirmation_email_sent",
  "shipped_email_sent",
  "delivered_email_sent",
  "internal_note",
  "production_files_generated",
  "files_sent_to_factory",
  "production_started",
  "production_finished",
  "received_from_factory",
  "qc_passed",
  "packed",
  "handed_to_carrier",
  "delivered",
] as const;
export type OrderEventType = (typeof ORDER_EVENT_TYPES)[number];
export const OrderEventTypeSchema = z.enum(ORDER_EVENT_TYPES);
